import { NgModule } from '@angular/core';
import { ActivatedRouteSnapshot, RouterModule, Routes } from '@angular/router';

// Re-run the resolver only when query params changed (filters, page, search),
// not when only the path changes (e.g. /services -> /services/42).
function rerunOnQueryParamsChange(from: ActivatedRouteSnapshot, to: ActivatedRouteSnapshot): boolean {
  return JSON.stringify(from.queryParams) !== JSON.stringify(to.queryParams);
}
import { AuthGuardService as AuthGuard } from './services';
import {
  SignInComponent,
  ClientsComponent,
  IdentityProviderComponent,
  AppsComponent,
  UsersComponent,
  OperatorsComponent,
  VehiclesComponent,
  TownsComponent,
  ServicesComponent,
  BookingsComponent,
  SetupComponent,
  AvailabilitiesComponent,
} from './pages';
import { UserRole } from './models/enums/userRole';
import { TripsComponent } from './pages/trips/trips.component';
import { AvailabilitiesResolver, BookingsResolver, CommunicationsResolver, ServiceInfosResolver, SetupResolver, TripsResolver, UsersResolver } from './resolvers';
import { bookingsHydrationGuard } from './guards/bookings-hydrate.guard';
import { usersHydrationGuard } from './guards/users-hydrate.guard';
import { servicesHydrationGuard } from './guards/services-hydrate.guard';
import { tripsHydrationGuard } from './guards/trips-hydrate.guard';
import { communicationsHydrationGuard } from './guards/communications-hydrate.guard';
import { availabilitiesHydrationGuard } from './guards/availabilities-hydrate.guard';
import { signInRedirectGuard } from './guards/sign-in-redirect.guard';
import { CommunicationsComponent } from './pages/communications/communications.component';

const routes: Routes = [
  { path: '', component: SignInComponent, canActivate: [signInRedirectGuard] },
  {
    path: 'clients',
    component: ClientsComponent,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.SUPERADMIN],
    },
  },
  {
    path: 'idp',
    component: IdentityProviderComponent,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.SUPERADMIN],
    },
  },
  {
    path: 'apps',
    component: AppsComponent,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.SUPERADMIN],
    },
  },
  {
    path: 'users',
    component: UsersComponent,
    canActivate: [AuthGuard, usersHydrationGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.OPERATOR,
      ],
    },
    resolve: { users: UsersResolver },
  },
  {
    path: 'drivers',
    component: UsersComponent,
    canActivate: [AuthGuard, usersHydrationGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.DRIVER_MANAGER,
      ],
    },
    resolve: { users: UsersResolver },
  },
  {
    path: 'suggestions/users',
    component: CommunicationsComponent,
    canActivate: [AuthGuard, communicationsHydrationGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.OPERATOR,
      ],
    },
    resolve: { suggestions: CommunicationsResolver },
  },
  {
    path: 'suggestions/users/:id',
    component: CommunicationsComponent,
    canActivate: [AuthGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.OPERATOR,
      ],
    },
    resolve: { suggestions: CommunicationsResolver },
  },
  {
    path: 'suggestions/drivers',
    component: CommunicationsComponent,
    canActivate: [AuthGuard, communicationsHydrationGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.DRIVER_MANAGER,
      ],
    },
    resolve: { suggestions: CommunicationsResolver },
  },
  {
    path: 'suggestions/drivers/:id',
    component: CommunicationsComponent,
    canActivate: [AuthGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.DRIVER_MANAGER,
      ],
    },
    resolve: { suggestions: CommunicationsResolver },
  },
  {
    path: 'messages/users',
    component: CommunicationsComponent,
    canActivate: [AuthGuard, communicationsHydrationGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.OPERATOR,
        UserRole.DRIVER_MANAGER,
      ],
    },
    resolve: { messages: CommunicationsResolver },
  },
  {
    path: 'messages/users/:id',
    component: CommunicationsComponent,
    canActivate: [AuthGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.OPERATOR,
        UserRole.DRIVER_MANAGER,
      ],
    },
    resolve: { messages: CommunicationsResolver },
  },
  {
    path: 'messages/drivers',
    component: CommunicationsComponent,
    canActivate: [AuthGuard, communicationsHydrationGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.OPERATOR,
        UserRole.DRIVER_MANAGER,
      ],
    },
    resolve: { messages: CommunicationsResolver },
  },
  {
    path: 'messages/drivers/:id',
    component: CommunicationsComponent,
    canActivate: [AuthGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.OPERATOR,
        UserRole.DRIVER_MANAGER,
      ],
    },
    resolve: { messages: CommunicationsResolver },
  },
  {
    path: 'users/:id',
    component: UsersComponent,
    canActivate: [AuthGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.OPERATOR,
      ],
    },
    resolve: { users: UsersResolver },
  },
  {
    path: 'drivers/:id',
    component: UsersComponent,
    canActivate: [AuthGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.DRIVER_MANAGER,
      ],
    },
    resolve: { users: UsersResolver },
  },
  {
    path: 'operators',
    component: OperatorsComponent,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.SUPERADMIN],
    },
  },
  {
    path: 'vehicles',
    component: VehiclesComponent,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.DRIVER_MANAGER],
    },
  },
  {
    path: 'towns',
    component: TownsComponent,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
  },
  {
    path: 'services',
    component: ServicesComponent,
    canActivate: [AuthGuard, servicesHydrationGuard],
    runGuardsAndResolvers: rerunOnQueryParamsChange,
    data: {
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
    resolve: { services: ServiceInfosResolver },
  },
  {
    path: 'services/new/setup',
    component: SetupComponent,
    canActivate: [ AuthGuard ],
    data: {
      new: true,
      wizard: true,
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
    resolve: { setups: SetupResolver },
  },
  {
    path: 'services/new/setup/backwards',
    component: SetupComponent,
    canActivate: [ AuthGuard ],
    data: {
      new: true,
      wizard: true,
      backwards: true,
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
    resolve: { setups: SetupResolver },
  },
  {
    path: 'services/:id',
    component: ServicesComponent,
    canActivate: [AuthGuard],
    runGuardsAndResolvers: rerunOnQueryParamsChange,
    data: {
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
    resolve: { services: ServiceInfosResolver },
  },
  {
    path: 'services/:id/:subRoute',
    component: ServicesComponent,
    canActivate: [AuthGuard],
    runGuardsAndResolvers: rerunOnQueryParamsChange,
    data: {
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
    resolve: { services: ServiceInfosResolver },
  },
  {
    path: 'services/:id/:subRoute/:routeId',
    component: ServicesComponent,
    canActivate: [AuthGuard],
    runGuardsAndResolvers: rerunOnQueryParamsChange,
    data: {
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
    resolve: { services: ServiceInfosResolver },
  },
  {
    path: 'services/:id/:subRoute/:routeId/design',
    component: ServicesComponent,
    canActivate: [AuthGuard],
    runGuardsAndResolvers: rerunOnQueryParamsChange,
    data: {
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
    resolve: { services: ServiceInfosResolver },
  },
  {
    path: 'services/:id/routes/:routeId/setups',
    component: SetupComponent,
    canActivate: [ AuthGuard ],
    data: {
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
    resolve: { setups: SetupResolver },
  },
  {
    path: 'services/:id/routes/:routeId/setups/:setupId',
    component: SetupComponent,
    canActivate: [ AuthGuard ],
    data: {
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
    resolve: { setups: SetupResolver },
  },
  {
    path: 'services/:id/routes/:routeId/setups/:setupId/availabilities',
    component: SetupComponent,
    canActivate: [ AuthGuard ],
    data: {
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
    resolve: { setups: SetupResolver },
  },
  {
    path: 'services/:id/routes/:routeId/availabilities',
    component: AvailabilitiesComponent,
    canActivate: [ AuthGuard, availabilitiesHydrationGuard ],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
    resolve: { availabilities: AvailabilitiesResolver },
  },
  {
    path: 'services/:id/:subRoute/:routeId/alterations',
    component: ServicesComponent,
    canActivate: [AuthGuard],
    runGuardsAndResolvers: rerunOnQueryParamsChange,
    data: {
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
    resolve: { services: ServiceInfosResolver },
  },
  {
    path: 'services/:id/:subRoute/:routeId/restrictions',
    component: ServicesComponent,
    canActivate: [AuthGuard],
    runGuardsAndResolvers: rerunOnQueryParamsChange,
    data: {
      roles: [UserRole.SUPERADMIN, UserRole.ADMIN],
    },
    resolve: { services: ServiceInfosResolver },
  },
  {
    path: 'trips',
    component: TripsComponent,
    canActivate: [AuthGuard, tripsHydrationGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.OPERATOR,
        UserRole.DRIVER_MANAGER,
      ],
    },
    resolve: { trips: TripsResolver },
    children: [
      {
        path: 'bookings/:id',
        component: TripsComponent,
        data: { bookingsOnly: true },
      },
      {
        path: ':id/bookings',
        component: TripsComponent,
      },
      {
        path: ':id',
        component: TripsComponent,
      },
    ],
  },
  {
    path: 'bookings',
    component: BookingsComponent,
    canActivate: [AuthGuard, bookingsHydrationGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.OPERATOR,
        UserRole.DRIVER_MANAGER,
      ],
    },
    resolve: { bookings: BookingsResolver },
  },
  {
    path: 'bookings/new',
    component: BookingsComponent,
    canActivate: [AuthGuard],
    data: {
      new: true,
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.OPERATOR,
        UserRole.DRIVER_MANAGER,
      ],
    },
    resolve: { bookings: BookingsResolver },
  },
  {
    path: 'bookings/users/:userId',
    component: BookingsComponent,
    canActivate: [AuthGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.OPERATOR,
        UserRole.DRIVER_MANAGER,
      ],
    },
    resolve: { bookings: BookingsResolver },
  },
  {
    path: 'bookings/users/:userId/:id',
    component: BookingsComponent,
    canActivate: [AuthGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.OPERATOR,
        UserRole.DRIVER_MANAGER,
      ],
    },
    resolve: { bookings: BookingsResolver },
  },
  {
    path: 'bookings/:id',
    component: BookingsComponent,
    canActivate: [AuthGuard],
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    data: {
      roles: [
        UserRole.SUPERADMIN,
        UserRole.ADMIN,
        UserRole.OPERATOR,
        UserRole.DRIVER_MANAGER,
      ],
    },
    resolve: { bookings: BookingsResolver },
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { onSameUrlNavigation: 'reload' })],
  exports: [RouterModule],
})
export class AppRoutingModule { }
